/**
 * Tournaments Hub — Browse, register, and view bracket tournaments.
 */
import { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, RefreshControl, ActivityIndicator, Modal, TextInput, Alert } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasAtLeast } from '@/lib/subscription';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, SPACE } from '@/constants/designTokens';
import { fetchTournaments, registerForTournament, createTournament, generateBracket, type Tournament } from '@/lib/tournaments';
import { ArrowLeft, Trophy, Users, Calendar, Zap, ChevronRight, Plus, Building2 } from 'lucide-react-native';

function TournamentCard({ tournament, onRegister, onTap, colors }: { tournament: Tournament; onRegister: (id: string) => void; onTap: (id: string) => void; colors: any }) {
  const isOpen = tournament.status === 'registration';
  const isActive = tournament.status === 'in_progress';
  const isComplete = tournament.status === 'completed';

  const statusConfig: Record<string, { label: string; color: string }> = {
    registration: { label: 'OPEN', color: colors.success || '#22c55e' },
    seeding: { label: 'SEEDING', color: colors.accent },
    in_progress: { label: 'LIVE', color: '#ef4444' },
    completed: { label: 'COMPLETED', color: colors.textTertiary },
    draft: { label: 'UPCOMING', color: colors.textSecondary },
  };

  const status = statusConfig[tournament.status] || statusConfig.draft;
  const formatLabels: Record<string, string> = {
    single_elimination: 'Single Elimination',
    double_elimination: 'Double Elimination',
    round_robin: 'Round Robin',
    swiss: 'Swiss',
  };

  return (
    <TouchableOpacity
      style={[styles.card, { backgroundColor: colors.surface, borderColor: isActive ? '#ef4444' + '40' : colors.border }]}
      activeOpacity={0.7}
      onPress={() => onTap(tournament.id)}
    >
      <View style={styles.cardHeader}>
        <Trophy size={18} color={colors.accent} />
        <View style={{ flex: 1 }}>
          <Text style={[styles.cardTitle, { color: colors.text, fontFamily: TYPO.heading.bold }]}>
            {tournament.name}
          </Text>
          <Text style={[styles.cardFormat, { color: colors.textSecondary }]}>
            {formatLabels[tournament.format] || tournament.format} · {tournament.bracket_size} players
          </Text>
        </View>
        <View style={[styles.statusPill, { backgroundColor: status.color + '20' }]}>
          <Text style={[styles.statusText, { color: status.color, fontFamily: TYPO.body.semibold }]}>
            {status.label}
          </Text>
        </View>
      </View>

      {tournament.description && (
        <Text style={[styles.cardDesc, { color: colors.textSecondary }]} numberOfLines={2}>
          {tournament.description}
        </Text>
      )}

      <View style={styles.cardMeta}>
        <View style={styles.metaItem}>
          <Calendar size={12} color={colors.textTertiary} />
          <Text style={[styles.metaText, { color: colors.textTertiary }]}>
            {new Date(tournament.starts_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </Text>
        </View>
        {tournament.prize_pool_cents > 0 && (
          <View style={styles.metaItem}>
            <Zap size={12} color={colors.accent} />
            <Text style={[styles.metaText, { color: colors.accent, fontFamily: TYPO.body.semibold }]}>
              ${(tournament.prize_pool_cents / 100).toFixed(0)} prize
            </Text>
          </View>
        )}
        {tournament.entry_fee_cents > 0 && (
          <View style={styles.metaItem}>
            <Text style={[styles.metaText, { color: colors.textTertiary }]}>
              ${(tournament.entry_fee_cents / 100).toFixed(0)} entry
            </Text>
          </View>
        )}
      </View>

      {isOpen && (
        <TouchableOpacity
          style={[styles.registerBtn, { backgroundColor: colors.accent }]}
          onPress={() => onRegister(tournament.id)}
        >
          <Text style={[styles.registerText, { color: '#000', fontFamily: TYPO.heading.bold }]}>
            REGISTER
          </Text>
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );
}

export default function TournamentsScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const c = theme.colors;
  const { profile } = useAuth();
  const { tier } = useSubscription();
  const isClub = hasAtLeast(tier, 'org_pro');
  const isCoachPlus = hasAtLeast(tier, 'coach_pro');
  const ADMIN_HANDLES = ['ufc4', 'ufc5', 'blazer23', 'raczor23'];
  const isAdmin = ADMIN_HANDLES.includes(profile?.handle ?? '');
  const canCreateTournament = isAdmin || isCoachPlus;

  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newFormat, setNewFormat] = useState<'single_elimination' | 'double_elimination' | 'round_robin' | 'swiss'>('single_elimination');
  const [newBracketSize, setNewBracketSize] = useState(16);
  const [newLiftCategory, setNewLiftCategory] = useState('');
  const [newStartDate, setNewStartDate] = useState('');
  const [linkToGym, setLinkToGym] = useState(false);
  const [newCustomRules, setNewCustomRules] = useState('');

  const load = useCallback(async () => {
    const data = await fetchTournaments();
    setTournaments(data);
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRegister = async (tournamentId: string) => {
    if (!profile?.id) return;
    await registerForTournament(tournamentId, profile.id);
    load();
  };

  const handleTap = (tournamentId: string) => {
    router.push(`/tournament/${tournamentId}` as any);
  };

  const handleCreate = async () => {
    if (!newName.trim()) { Alert.alert('Missing Info', "Enter a tournament name."); return; }
    if (!newStartDate.trim()) { Alert.alert('Error', 'Start date is required (YYYY-MM-DD)'); return; }
    setCreating(true);
    const id = await createTournament({
      name: newName.trim(),
      format: newFormat,
      bracket_size: newBracketSize,
      lift_category: newLiftCategory || undefined,
      starts_at: new Date(newStartDate).toISOString(),
      ...(isClub && linkToGym && profile?.gym_id ? { gym_id: profile.gym_id } : {}),
      ...(isClub && newCustomRules.trim() ? { custom_rules: newCustomRules.trim() } : {}),
    });
    setCreating(false);
    if (id) {
      setShowCreate(false);
      setNewName(''); setNewLiftCategory(''); setNewStartDate('');
      setLinkToGym(false); setNewCustomRules('');
      load();
    } else {
      Alert.alert('Oops', "Couldn't create the tournament. Please try again.");
    }
  };

  const active = tournaments.filter(t => t.status === 'in_progress');
  const open = tournaments.filter(t => t.status === 'registration');
  const upcoming = tournaments.filter(t => t.status === 'draft');
  const past = tournaments.filter(t => t.status === 'completed');

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backBtn}>
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>TOURNAMENTS</Text>
        {canCreateTournament && (
          <TouchableOpacity onPress={() => setShowCreate(true)} style={styles.backBtn}>
            <Plus size={22} color={c.accent} />
          </TouchableOpacity>
        )}
      </View>

      {/* Admin Create Modal */}
      <Modal visible={showCreate} animationType="slide" transparent>
        <View style={[styles.modalOverlay]}>
          <View style={[styles.modalContent, { backgroundColor: c.background, borderColor: c.border }]}>
            <Text style={[styles.modalTitle, { color: c.text, fontFamily: TYPO.heading.bold }]}>CREATE TOURNAMENT</Text>

            <TextInput style={[styles.input, { color: c.text, borderColor: c.border }]} placeholder="Tournament name" placeholderTextColor={c.textTertiary} value={newName} onChangeText={setNewName} />

            <Text style={[styles.inputLabel, { color: c.textSecondary }]}>Format</Text>
            <View style={styles.pickerRow}>
              {(['single_elimination', 'double_elimination', 'round_robin', 'swiss'] as const).map(f => (
                <TouchableOpacity key={f} style={[styles.pickerChip, { borderColor: newFormat === f ? c.accent : c.border, backgroundColor: newFormat === f ? c.accent + '20' : 'transparent' }]} onPress={() => setNewFormat(f)}>
                  <Text style={[styles.pickerChipText, { color: newFormat === f ? c.accent : c.textSecondary }]}>{f.replace(/_/g, ' ')}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={[styles.inputLabel, { color: c.textSecondary }]}>Bracket Size</Text>
            <View style={styles.pickerRow}>
              {[8, 16, 32, 64].map(s => (
                <TouchableOpacity key={s} style={[styles.pickerChip, { borderColor: newBracketSize === s ? c.accent : c.border, backgroundColor: newBracketSize === s ? c.accent + '20' : 'transparent' }]} onPress={() => setNewBracketSize(s)}>
                  <Text style={[styles.pickerChipText, { color: newBracketSize === s ? c.accent : c.textSecondary }]}>{s}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <TextInput style={[styles.input, { color: c.text, borderColor: c.border }]} placeholder="Lift category (optional)" placeholderTextColor={c.textTertiary} value={newLiftCategory} onChangeText={setNewLiftCategory} />
            <TextInput style={[styles.input, { color: c.text, borderColor: c.border }]} placeholder="Start date (YYYY-MM-DD)" placeholderTextColor={c.textTertiary} value={newStartDate} onChangeText={setNewStartDate} />

            {/* Club (org_pro) — Link to Gym & Custom Rules */}
            {isClub && (
              <>
                <TouchableOpacity
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: SPACE.xs }}
                  onPress={() => setLinkToGym(!linkToGym)}
                >
                  <View style={{ width: 20, height: 20, borderWidth: 1, borderRadius: 4, borderColor: linkToGym ? c.accent : c.border, backgroundColor: linkToGym ? c.accent : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
                    {linkToGym && <Text style={{ color: '#000', fontSize: 12, fontWeight: 'bold' }}>✓</Text>}
                  </View>
                  <Building2 size={14} color={c.accent} />
                  <Text style={{ color: c.text, fontSize: 13, fontFamily: TYPO.body.semibold }}>Link to My Gym</Text>
                </TouchableOpacity>
                <TextInput
                  style={[styles.input, { color: c.text, borderColor: c.border }]}
                  placeholder="Custom rules (optional)"
                  placeholderTextColor={c.textTertiary}
                  value={newCustomRules}
                  onChangeText={setNewCustomRules}
                  multiline
                />
              </>
            )}

            <View style={{ flexDirection: 'row', gap: SPACE.sm, marginTop: SPACE.md }}>
              <TouchableOpacity style={[styles.modalBtn, { borderColor: c.border, flex: 1 }]} onPress={() => setShowCreate(false)}>
                <Text style={[{ color: c.textSecondary, fontFamily: TYPO.heading.bold, fontSize: 13 }]}>CANCEL</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.modalBtn, { backgroundColor: c.accent, borderColor: c.accent, flex: 1 }]} onPress={handleCreate} disabled={creating}>
                {creating ? <ActivityIndicator color="#000" /> : <Text style={[{ color: '#000', fontFamily: TYPO.heading.bold, fontSize: 13 }]}>CREATE</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <ScrollView
        contentContainerStyle={styles.content}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} tintColor={c.accent} />}
      >
        {loading ? (
          <ActivityIndicator color={c.accent} style={{ padding: 40 }} />
        ) : tournaments.length === 0 ? (
          <View style={[styles.empty, { borderColor: c.border }]}>
            <Trophy size={32} color={c.textTertiary} />
            <Text style={[styles.emptyTitle, { color: c.textSecondary, fontFamily: TYPO.heading.bold }]}>
              NO TOURNAMENTS YET
            </Text>
            <Text style={[styles.emptyDesc, { color: c.textTertiary }]}>
              No tournaments scheduled yet. Check back soon!
            </Text>
          </View>
        ) : (
          <>
            {active.length > 0 && (
              <Section label="🔴 LIVE" colors={c}>
                {active.map(t => <TournamentCard key={t.id} tournament={t} onRegister={handleRegister} onTap={handleTap} colors={c} />)}
              </Section>
            )}
            {open.length > 0 && (
              <Section label="REGISTRATION OPEN" colors={c}>
                {open.map(t => <TournamentCard key={t.id} tournament={t} onRegister={handleRegister} onTap={handleTap} colors={c} />)}
              </Section>
            )}
            {upcoming.length > 0 && (
              <Section label="UPCOMING" colors={c}>
                {upcoming.map(t => <TournamentCard key={t.id} tournament={t} onRegister={handleRegister} onTap={handleTap} colors={c} />)}
              </Section>
            )}
            {past.length > 0 && (
              <Section label="COMPLETED" colors={c}>
                {past.map(t => <TournamentCard key={t.id} tournament={t} onRegister={handleRegister} onTap={handleTap} colors={c} />)}
              </Section>
            )}
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

function Section({ label, colors, children }: { label: string; colors: any; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={[styles.sectionLabel, { color: colors.accent, fontFamily: TYPO.heading.bold }]}>{label}</Text>
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, gap: 12 },
  backBtn: { padding: 4 },
  title: { fontSize: FONT.xl, letterSpacing: 1, flex: 1 },
  content: { padding: 16, gap: SPACE.lg, paddingBottom: 40 },
  section: { gap: SPACE.sm },
  sectionLabel: { fontSize: 11, letterSpacing: 1.5 },
  card: { borderWidth: 1, borderRadius: RADIUS.xs, padding: 16, gap: SPACE.sm },
  cardHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  cardTitle: { fontSize: FONT.base },
  cardFormat: { fontSize: 12, marginTop: 2 },
  cardDesc: { fontSize: FONT.sm, lineHeight: 20 },
  cardMeta: { flexDirection: 'row', gap: SPACE.md },
  metaItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  metaText: { fontSize: 11 },
  statusPill: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: RADIUS.xs },
  statusText: { fontSize: 10, letterSpacing: 0.5 },
  registerBtn: { paddingVertical: 12, alignItems: 'center', borderRadius: RADIUS.xs, marginTop: 4 },
  registerText: { fontSize: 13, letterSpacing: 1 },
  empty: { padding: 40, alignItems: 'center', gap: SPACE.md, borderWidth: 1, borderRadius: RADIUS.xs, borderStyle: 'dashed' },
  emptyTitle: { fontSize: 14, letterSpacing: 1 },
  emptyDesc: { fontSize: 12, textAlign: 'center', lineHeight: 18 },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'center', padding: 24 },
  modalContent: { borderWidth: 1, borderRadius: RADIUS.xs, padding: 20, gap: SPACE.sm },
  modalTitle: { fontSize: FONT.lg, letterSpacing: 1, marginBottom: 4 },
  input: { borderWidth: 1, borderRadius: RADIUS.xs, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14 },
  inputLabel: { fontSize: 12, marginTop: 4 },
  pickerRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  pickerChip: { borderWidth: 1, borderRadius: RADIUS.xs, paddingHorizontal: 10, paddingVertical: 6 },
  pickerChipText: { fontSize: 11, textTransform: 'capitalize' },
  modalBtn: { borderWidth: 1, borderRadius: RADIUS.xs, paddingVertical: 12, alignItems: 'center' },
});
